import React, { useState, useEffect } from 'react';
import {
  Overlay,
  ModalContainer,
  ContentWrapper,
  Title,
  Subtitle,
  PhoneImage,
  ActionsWrapper,
  PrimaryButton,
  PrimaryButtonText,
  PrimaryButtonSubtext,
  SecondaryButton
} from './styles';
import IosInstructionsModal from './IosInstructionsModal';
import { usePwaInstall, isPwa, isIOS, isMobileDevice } from '../../utils/pwaUtils';

// Placeholder phone mockup image - you can replace with actual asset
import PhoneMockup from '../../resources/imgs/phone-pwa-mockup.png';

interface PwaInstallBannerProps {
  onDismiss?: () => void;
}

const PWA_BANNER_STORAGE_KEY = 'pwa_install_banner_dismissed';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const shouldShowBanner = (): boolean => {
  const now = Date.now();
  const stored = localStorage.getItem(PWA_BANNER_STORAGE_KEY);

  if (!stored) return true;

  try {
    const { expiry } = JSON.parse(stored);
    if (now > expiry) return true;
    return false;
  } catch {
    return true;
  }
};

const setDismissedInLocalStorage = () => {
  const expiry = Date.now() + SEVEN_DAYS_MS;
  localStorage.setItem(PWA_BANNER_STORAGE_KEY, JSON.stringify({ expiry }));
};

const PwaInstallBanner: React.FC<PwaInstallBannerProps> = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const { deferredPrompt, promptInstall, isInstalled } = usePwaInstall();

  useEffect(() => {
    // Check if should show banner
    const shouldShow =
      isMobileDevice() &&
      !isPwa() &&
      !isInstalled &&
      shouldShowBanner();

    setIsVisible(shouldShow);
  }, [isInstalled]);

  const handleInstallClick = () => {
    if (isIOS()) {
      setShowIosInstructions(true);
    } else if (deferredPrompt) {
      promptInstall();
      handleDismiss();
    } else {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setDismissedInLocalStorage();
    setIsVisible(false);
    onDismiss?.();
  };

  const handleIosInstructionsClose = () => {
    setShowIosInstructions(false);
    handleDismiss();
  };

  if (!isVisible) return null;

  return (
    <>
      <Overlay onClick={handleDismiss}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ContentWrapper>
            <Title>
              Melhore sua <span>experiência!</span>
            </Title>

            <Subtitle>
              Instale nosso app para acessar uma versão otimizada para uso mobile.
            </Subtitle>

            <PhoneImage
              src={PhoneMockup}
              alt="App mobile preview"
              onError={(e) => {
                // Hide image if not found
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />

            <Subtitle>
              Realize análises e contratação de forma simplificada!
            </Subtitle>
          </ContentWrapper>

          <ActionsWrapper>
            <PrimaryButton onClick={handleInstallClick}>
              <PrimaryButtonText>Acessar versão mobile</PrimaryButtonText>
              <PrimaryButtonSubtext>Recomendado</PrimaryButtonSubtext>
            </PrimaryButton>

            <SecondaryButton onClick={handleDismiss}>
              Continuar pelo navegador
            </SecondaryButton>
          </ActionsWrapper>
        </ModalContainer>
      </Overlay>

      {showIosInstructions && isIOS() && (
        <IosInstructionsModal onClose={handleIosInstructionsClose} />
      )}
    </>
  );
};

export default PwaInstallBanner;
